import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";
import {
	labelConhecimentoCategory,
	normalizeConhecimentoCategory,
} from "../../pages/OpenAi/conhecimentoCategories";

const DeleteIcon = ({ className = "size-4" }) => (
	<svg
		className={className}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
		<line x1="10" y1="11" x2="10" y2="17" />
		<line x1="14" y1="11" x2="14" y2="17" />
	</svg>
);

const EditIcon = ({ className = "size-4" }) => (
	<svg
		className={className}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<path d="M12 20h9" />
		<path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" />
	</svg>
);

const ChevronIcon = ({ open, className = "size-4" }) => (
	<svg
		className={`${className} transition-transform ${open ? "rotate-180" : ""}`}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<path d="m6 9 6 6 6-6" />
	</svg>
);

function formatDate(raw) {
	if (!raw) return null;
	const d = new Date(raw);
	if (Number.isNaN(d.getTime())) return null;
	return d.toLocaleDateString("pt-BR", {
		day: "2-digit",
		month: "short",
		year: "numeric",
	});
}

function sizeLabel(len) {
	if (len < 200) {
		return {
			label: "Curto",
			className: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
		};
	}
	if (len < 800) {
		return {
			label: "Médio",
			className: "bg-sky-500/15 text-sky-800 dark:text-sky-300",
		};
	}
	return {
		label: "Longo",
		className: "bg-amber-500/15 text-amber-800 dark:text-amber-300",
	};
}

export default function ConhecimentoCard({
	item,
	viewMode = "grid",
	onDeleted,
	selectItem,
	openModalEdit,
	setTitle,
	setContent,
	setCategory,
	setOpen,
}) {
	const [openDelete, setOpenDelete] = useState(false);
	const [expanded, setExpanded] = useState(false);

	const contentText = String(item?.content || "").trim();
	const contentLen = contentText.length;
	const size = useMemo(() => sizeLabel(contentLen), [contentLen]);
	const dateLabel = formatDate(item?.updatedAt || item?.createdAt);
	const needsClamp = contentLen > 180;
	const isList = viewMode === "list";
	const categoryLabel = labelConhecimentoCategory(item?.category);

	const handleDelete = async () => {
		try {
			await api.delete(`/excluirConhecimento/${item.id}`);
			toast.success("Excluído com sucesso!");
			if (onDeleted) onDeleted(item.id);
			setOpenDelete(false);
		} catch (e) {
			toastError("Erro ao deletar");
		}
	};

	const handleEdit = () => {
		selectItem(item);
		setTitle(item.title);
		setContent(item.content);
		if (setCategory) {
			setCategory(normalizeConhecimentoCategory(item.category));
		}
		openModalEdit(true);
		setOpen(true);
	};

	const deleteModal = (
		<ConfirmationModal
			title="Excluir conhecimento"
			open={openDelete}
			onClose={() => setOpenDelete(false)}
			onConfirm={handleDelete}
		>
			Tem certeza que deseja excluir este item? Essa ação não pode ser desfeita.
		</ConfirmationModal>
	);

	if (isList) {
		return (
			<div className="group border-b border-default-200 last:border-b-0 transition-colors hover:bg-default-50/80 dark:border-default-800 dark:hover:bg-default-900/60">
				{deleteModal}

				<div className="flex flex-col gap-3 px-4 py-3.5 sm:flex-row sm:items-center sm:gap-4 sm:px-5">
					<div className="hidden h-10 w-1 shrink-0 rounded-full bg-primary/70 sm:block" />

					<div className="min-w-0 flex-1">
						<div className="flex flex-wrap items-center gap-2">
							<span className="inline-flex items-center rounded-md bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
								{categoryLabel}
							</span>
							<span
								className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold ${size.className}`}
							>
								{size.label}
							</span>
							<span className="text-[11px] tabular-nums text-default-400">
								#{item.id}
							</span>
						</div>

						<button
							type="button"
							onClick={() => setExpanded((v) => !v)}
							className="mt-1.5 w-full text-left"
						>
							<h3 className="m-0 truncate text-[15px] font-semibold leading-snug text-default-900 dark:text-default-50">
								{item.title}
							</h3>
							{!expanded ? (
								<p className="mt-1 mb-0 line-clamp-1 text-sm text-default-500 dark:text-default-400">
									{contentText || "Sem conteúdo"}
								</p>
							) : null}
						</button>

						{expanded ? (
							<div className="mt-2 rounded-xl border border-default-200 bg-white px-3.5 py-3 dark:border-default-700 dark:bg-default-950/40">
								<p className="m-0 whitespace-pre-wrap break-words text-sm leading-6 text-default-600 dark:text-default-300">
									{contentText || "—"}
								</p>
							</div>
						) : null}

						<div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-default-400">
							{dateLabel ? <span>Atualizado em {dateLabel}</span> : null}
							<span className="tabular-nums">
								{contentLen.toLocaleString("pt-BR")} caracteres
							</span>
							{needsClamp ? (
								<button
									type="button"
									onClick={() => setExpanded((v) => !v)}
									className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"
								>
									{expanded ? "Recolher" : "Expandir"}
									<ChevronIcon open={expanded} className="size-3.5" />
								</button>
							) : null}
						</div>
					</div>

					<div className="flex shrink-0 items-center gap-2 self-end sm:self-center">
						<button
							type="button"
							onClick={handleEdit}
							className="inline-flex items-center gap-1.5 rounded-lg border border-default-200 bg-white px-3 py-1.5 text-xs font-medium text-default-700 transition hover:border-primary/20 hover:bg-primary/10 hover:text-primary dark:border-default-700 dark:bg-default-900 dark:text-default-200"
						>
							<EditIcon className="size-3.5" />
							Editar
						</button>
						<button
							type="button"
							onClick={() => setOpenDelete(true)}
							className="inline-flex size-8 items-center justify-center rounded-lg bg-danger/10 text-danger transition-colors hover:bg-danger/20 dark:bg-danger/15 dark:hover:bg-danger/25"
							title="Excluir"
						>
							<DeleteIcon className="size-4 text-danger" />
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="card h-full border border-default-200/80 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/20 hover:shadow-md dark:border-white/10">
			{deleteModal}

			<div className="card-body flex h-full flex-col gap-4">
				<div className="min-w-0">
					<div className="mb-2 flex flex-wrap items-center gap-2">
						<span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary">
							{categoryLabel}
						</span>
						<span
							className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${size.className}`}
						>
							{size.label}
						</span>
						<span className="text-[11px] tabular-nums text-default-400">
							{contentLen.toLocaleString("pt-BR")} caracteres
						</span>
					</div>

					<h3 className="m-0 text-base font-semibold leading-snug text-default-800 dark:text-default-100">
						{item.title}
					</h3>

					{dateLabel ? (
						<p className="mt-1 mb-0 text-xs text-default-400">
							Atualizado em {dateLabel}
						</p>
					) : null}

					<div className="mt-3 rounded-2xl border border-default-200 bg-default-50 p-4 dark:border-default-700 dark:bg-default-900">
						<p
							className={`m-0 whitespace-pre-wrap break-words text-sm leading-6 text-default-600 dark:text-default-300 ${
								!expanded && needsClamp ? "line-clamp-4" : ""
							}`}
						>
							{contentText || "—"}
						</p>
						{needsClamp ? (
							<button
								type="button"
								onClick={() => setExpanded((v) => !v)}
								className="mt-2 text-xs font-semibold text-primary hover:underline"
							>
								{expanded ? "Mostrar menos" : "Ver mais"}
							</button>
						) : null}
					</div>
				</div>

				<div className="mt-auto flex items-center justify-between gap-2 border-t border-default-200 pt-4 dark:border-default-700">
					<p className="m-0 text-xs text-default-500 dark:text-default-400">
						ID #{item.id}
					</p>
					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={handleEdit}
							className="btn btn-sm border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
						>
							Editar
						</button>
						<button
							type="button"
							onClick={() => setOpenDelete(true)}
							className="inline-flex size-8 items-center justify-center rounded-lg bg-danger/10 text-danger transition-colors hover:bg-danger/20 dark:bg-danger/15 dark:hover:bg-danger/25"
							title="Excluir"
						>
							<DeleteIcon className="size-4 text-danger" />
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
